import { useCallback, useContext, useMemo, useState } from "react"
import { enqueueSnackbar } from "notistack"
import exportClient, { getExportClientUrl } from "../api/exportClient"
import {
  getBooleanFilters,
  getEntityFilters,
  getCommonFilters,
  getTimeDimensions,
  getRequestFilters,
  getExportPath,
  isExportTableActionDisabled,
  downloadLinkByForm,
  getExportFeatureFlagParams,
  getTableOrderRequest,
  getAdditionalFeatureFlagFilters,
  getWorkkeysReplaceTerm
} from "../helpers"
import { ActionOption } from "../types"
import { FiltersState } from "../states"
import {
  DETAILS_REQUEST_PARAMS,
  EXCLUDE_EXPORT_FILTERS_BY_ROLE,
  REQUEST_FILTER_MAP,
  TIME_DIMENSIONS
} from "../constants"
// import { REQUEST_PARAMS } from "../constants/tables"
import { ReportingContext } from "../providers/ReportingProvider"
import { FeatureFlagContext } from "../../mp-core/FeatureFlagProvider"
import { useSelector } from "react-redux"
import { RootState } from "../../mp-core/redux/store"
import { User } from "../../mp-database"
import { getAdditionalFiltersByRoleAndPage } from "../helpers/index"
import { UseTableState } from "../components/Table"
import { csvDownload } from "../../../services/generic-services/views/viewQueryResolvers"
import {
  SpecificExportCallbackArgsType,
  assignmentRecordStudentsExportCallback
} from "../helpers/specificExportCallbacks"
import {
  getGraphQLViewFilters
} from "../helpers/graphqlViewFilters"
import { FEATURE_FLAG_OVERRIDE } from "./fileExportFeatureFlagOverrides"

const downloadGQLCSVFile = (output: string, fileName: string) => {
  // TODO: we should change this to a temporary firebase storage file via signed link (10 minutes?)
  const linkSrc = `data:text/csv;base64,${output}`
  const downloadLink = document.createElement("a")
  downloadLink.href = linkSrc
  downloadLink.download = `${fileName || "File"}.csv`
  downloadLink.target = "_self"

  document.body.appendChild(downloadLink)
  downloadLink.click()
  document.body.removeChild(downloadLink)
}

export const SPECIFIC_FILE_EXPORTS: Record<
  string,
  Record<
    string,
    {
      fileName: string
      callback: (args: SpecificExportCallbackArgsType) => Promise<string | null>
    }
  >
> = {
  "teaching-tools": {
    "assignment-record-students": {
      fileName: `Assignment Record ${getWorkkeysReplaceTerm("students", true)}`,
      callback: assignmentRecordStudentsExportCallback
    }
  }
}

export const useFileExport = (args: {
  page: string
  subpage: string
  filters: FiltersState
  tableState: UseTableState
  subpageView?: string
  entityId?: string
  entityProp?: string
}): ActionOption => {
  const {
    page,
    subpage,
    filters,
    tableState,
    subpageView,
    entityId,
    entityProp
  } = args

  const { featureFlags } = useContext(FeatureFlagContext)
  const {
    reportingTableData: { requestParams: REQUEST_PARAMS },
    reportingQuestionsProgramsAndCourses
  } = useContext(ReportingContext)

  const { user } = useSelector((state: RootState) => ({
    user: state.users.testUser as User
  }))

  const excludeFilters = [
    ...((EXCLUDE_EXPORT_FILTERS_BY_ROLE[page]?.[subpage] ?? {})[user.role] ||
      []),
    ...(page === "organization" && subpage === "organizations" ? ["state"] : [])
  ]

  const [loading, setLoading] = useState(false)

  const entityIdProp =
    entityProp ??
    (entityId && subpageView
      ? DETAILS_REQUEST_PARAMS[page]?.[subpageView]?.dimensions?.[0]
      : "id")

  const queryFilters = useMemo(
    () => [
      ...getCommonFilters(
        filters,
        REQUEST_FILTER_MAP[page]?.[subpage],
        !!excludeFilters?.length
          ? {
              exclude: [...excludeFilters]
            }
          : null
      ),
      ...getBooleanFilters(filters, REQUEST_FILTER_MAP[page]?.[subpage]),
      ...(entityId ? getEntityFilters(entityIdProp, entityId) : []),
      ...(REQUEST_PARAMS[page]?.[subpage]?.filters || []),
      ...getAdditionalFiltersByRoleAndPage({
        page,
        subpage,
        userRole: user.role,
        filters
      }),
      ...getAdditionalFeatureFlagFilters({
        page,
        subpage,
        filters,
        featureFlags,
        reportingQuestionsProgramsAndCourses
      })
    ],
    [
      page,
      subpage,
      filters,
      entityId,
      entityIdProp,
      excludeFilters,
      REQUEST_PARAMS,
      user.role,
      featureFlags,
      reportingQuestionsProgramsAndCourses
    ]
  )

  const hasFilters = queryFilters.some(filter => !!filter.values || !!filter)

  const timeDimensions = useMemo(
    () =>
      getTimeDimensions(filters.date, subpage, TIME_DIMENSIONS[subpage])
        ?.timeDimensions,
    [filters, subpage]
  )

  const order = useMemo(
    () =>
      getTableOrderRequest({
        page,
        subpage,
        tableState
      }),
    [page, subpage, tableState]
  )

  const disabled = isExportTableActionDisabled(page, subpage, filters)

  const featureOverride = FEATURE_FLAG_OVERRIDE?.[page]?.[subpage] ?? undefined

  const callback = useCallback(async () => {
    if (loading || disabled) return

    const specificExportData = SPECIFIC_FILE_EXPORTS[page]?.[subpage]

    setLoading(true)

    if (!!specificExportData) {
      try {
        const output = await specificExportData.callback({
          filters,
          tableState,
          entityId
        })

        if (!output) {
          throw Error("Empty output.")
        }

        downloadGQLCSVFile(output, specificExportData.fileName)
      } catch (error) {
        console.error("Failed to fetch data:", error)
        enqueueSnackbar(
          "An error occurred. Please exit the page then try again. If the error persists, contact support for help.",
          { variant: "error" }
        )
      } finally {
        setLoading(false)
      }

      return
    }

    if (featureOverride && featureFlags[featureOverride.featureFlag]) {
      if (featureOverride.type === "graphql") {
        try {
          const allFilters = getGraphQLViewFilters({
            filters,
            entityId,
            requestParam: entityIdProp,
            subpageView
          })
          const data = await csvDownload({
            name: featureOverride.viewName,
            filters: allFilters
          })

          if (data?.output) {
            downloadGQLCSVFile(data.output, featureOverride.fileName)
          } else {
            enqueueSnackbar("No export data was returned. Please try again.", {
              variant: "error"
            })
          }
        } catch (error) {
          console.error("Failed to fetch data:", error)
          enqueueSnackbar(
            "An error occurred. Please exit the page then try again. If the error persists, contact support for help.",
            { variant: "error" }
          )
        } finally {
          setLoading(false)
        }
        return
      }
    }

    try {
      const tokenResponse = await exportClient.get(
        getExportPath(page, subpage),
        {
          responseType: "json",
          params: {
            ...getRequestFilters(filters),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(hasFilters ? { filters: JSON.stringify(queryFilters) } : {}),
            ...(!!order ? { order: JSON.stringify(order) } : {}),
            timeDimensions: JSON.stringify(timeDimensions),
            ...getExportFeatureFlagParams(page, subpage, featureFlags)
          }
        }
      )

      const fileUrlToken = tokenResponse.data

      if (!fileUrlToken) return

      downloadLinkByForm(
        `${getExportClientUrl()}/execute-download`,
        fileUrlToken
      )
    } catch (error) {
      console.error("Failed to fetch data:", error)
      enqueueSnackbar(
        "An error occurred. Please exit the page then try again. If the error persists, contact support for help.",
        { variant: "error" }
      )
    } finally {
      setLoading(false)
    }
  }, [
    loading,
    disabled,
    page,
    subpage,
    filters,
    hasFilters,
    queryFilters,
    order,
    timeDimensions,
    tableState,
    entityId,
    entityIdProp,
    subpageView,
    featureFlags,
    featureOverride
  ])

  return { action: callback, loading, disabled }
}
